import React from "react";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";

// Admin SKU Toggle — manage which Frame/Fabric SKUs are hidden from
// galleries / PDP / search. Apply queues a SKU TOGGLE APPLY job on lane
// Id=2 in DataSync (see Fairfield.Services/Services/IntegrationService).
// During the ~2 min the job takes to run, both galleries and search keep
// showing the SKUs (intentional — the cache invalidate + index rebuild
// land together).

const POLL_INTERVAL_MS = 5000;

export default class ManageSkuStatusGrid extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      entity: "Frame",                       // 'Frame' | 'Fabric'
      rows: [],                              // current disabled rows (from API)
      pendingAdds: [],                       // [{sku, name, notes}]
      pendingReEnable: {},                   // { [`${type}|${sku}`]: true }
      notesEdits: {},                        // { [`${type}|${sku}`]: edited notes }
      searchQuery: "",
      searchHits: [],
      lookupBusy: false,
      status: { state: "Idle", lastRunTime: "", blockedByOtherLane: false },
      loading: false,
      submitting: false,
      errorMessage: "",
      // After Apply, the diff is frozen on screen (controls disabled,
      // pending preview kept visible) until the job goes back to Idle.
      // Then we clear the pending state and reload rows from the server.
      submittedAt: null,
      // After the grid refreshes (job hit Idle), keep showing a
      // "search index still propagating" note for ~3 more minutes —
      // because the Azure indexer continues async after the job's
      // ResetSearchIndexAsync returns 202.
      lastCompletedAt: null
    };
    this._lookupTimer = null;
    this._statusTimer = null;
  }

  componentDidMount() {
    this.loadRows();
    this.loadStatus();
    this._statusTimer = setInterval(() => this.loadStatus(), POLL_INTERVAL_MS);
  }

  componentWillUnmount() {
    if (this._statusTimer) clearInterval(this._statusTimer);
    if (this._lookupTimer) clearTimeout(this._lookupTimer);
  }

  // ── Data loading ────────────────────────────────────────────────────────

  loadRows = async () => {
    this.setState({ loading: true, errorMessage: "" });
    try {
      const res = await fetch(`/api/Admin/ToggledSku/List?type=${this.state.entity}`);
      if (!res.ok) throw new Error("Failed to load disabled list");
      const data = await res.json();
      this.setState({ rows: data || [], loading: false });
    } catch (err) {
      this.setState({ loading: false, errorMessage: err.message || "Load failed" });
    }
  };

  loadStatus = async () => {
    try {
      const res = await fetch("/api/Admin/SkuToggleStatus");
      if (!res.ok) return;
      const data = await res.json();
      const prevState = this.state.status.state;
      this.setState({ status: data });
      // When transitioning from Processing → Idle, refresh the rows so the
      // grid reflects the just-applied diff. Also clear any frozen pending
      // state from a recent Apply.
      const wasProcessing = prevState === "Processing" || prevState === "Queued";
      if (wasProcessing && data.state === "Idle") {
        this.setState({
          pendingAdds: [],
          pendingReEnable: {},
          notesEdits: {},
          submittedAt: null,
          lastCompletedAt: Date.now()
        });
        this.loadRows();
      }
    } catch { /* swallow — next tick retries */ }
  };

  // ── Entity radio ────────────────────────────────────────────────────────

  onEntityChange = (e) => {
    const entity = e.target.value;
    this.setState({
      entity,
      rows: [],
      pendingAdds: [],
      pendingReEnable: {},
      notesEdits: {},
      searchQuery: "",
      searchHits: []
    }, () => this.loadRows());
  };

  // ── Search & Add ────────────────────────────────────────────────────────

  onSearchChange = (e) => {
    const q = e.target.value;
    this.setState({ searchQuery: q });
    if (this._lookupTimer) clearTimeout(this._lookupTimer);
    if (!q || q.trim().length < 2) {
      this.setState({ searchHits: [] });
      return;
    }
    this._lookupTimer = setTimeout(() => this.runLookup(q), 250);
  };

  runLookup = async (q) => {
    this.setState({ lookupBusy: true });
    try {
      const res = await fetch(`/api/Admin/ToggledSku/Lookup?type=${this.state.entity}&q=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error("Lookup failed");
      const data = await res.json();
      this.setState({ searchHits: data || [], lookupBusy: false });
    } catch {
      this.setState({ searchHits: [], lookupBusy: false });
    }
  };

  addSkuToDisabled = (hit) => {
    const key = `${this.state.entity}|${hit.sku}`;
    // Already in current disabled rows? Just clear any pending re-enable.
    const alreadyDisabled = this.state.rows.some(r =>
      r.sku.toUpperCase() === hit.sku.toUpperCase() && r.type === this.state.entity);
    if (alreadyDisabled) {
      const pendingReEnable = { ...this.state.pendingReEnable };
      delete pendingReEnable[key];
      this.setState({ pendingReEnable, searchQuery: "", searchHits: [] });
      return;
    }
    // Already in pending additions?
    if (this.state.pendingAdds.some(p => p.sku.toUpperCase() === hit.sku.toUpperCase())) {
      this.setState({ searchQuery: "", searchHits: [] });
      return;
    }
    this.setState({
      pendingAdds: [...this.state.pendingAdds, { sku: hit.sku, name: hit.name, notes: "" }],
      searchQuery: "",
      searchHits: []
    });
  };

  removePendingAdd = (sku) => {
    this.setState({
      pendingAdds: this.state.pendingAdds.filter(p => p.sku !== sku)
    });
  };

  setPendingAddNotes = (sku, notes) => {
    this.setState({
      pendingAdds: this.state.pendingAdds.map(p =>
        p.sku === sku ? { ...p, notes } : p
      )
    });
  };

  // ── Existing-row checkbox (re-enable) ───────────────────────────────────

  toggleReEnable = (row) => {
    const key = `${row.type}|${row.sku}`;
    const pendingReEnable = { ...this.state.pendingReEnable };
    if (pendingReEnable[key]) delete pendingReEnable[key];
    else pendingReEnable[key] = true;
    this.setState({ pendingReEnable });
  };

  // ── Inline notes editing on existing rows ───────────────────────────────

  setExistingRowNotes = (row, notes) => {
    const key = `${row.type}|${row.sku}`;
    this.setState({
      notesEdits: { ...this.state.notesEdits, [key]: notes }
    });
  };

  getExistingRowNotes = (row) => {
    const key = `${row.type}|${row.sku}`;
    return this.state.notesEdits[key] !== undefined
      ? this.state.notesEdits[key]
      : (row.notes || "");
  };

  // ── Pending changes calc ────────────────────────────────────────────────

  pendingDiff = () => {
    const reEnableEntries = this.state.rows
      .filter(r => this.state.pendingReEnable[`${r.type}|${r.sku}`])
      .map(r => ({
        sku: r.sku,
        type: r.type,
        notes: this.getExistingRowNotes(r)
      }));

    // Existing rows where notes changed but not pending re-enable —
    // these stay Disabled but get their note updated. Treat as a
    // Disabled-write to UPSERT the new notes.
    const noteOnlyDisables = this.state.rows
      .filter(r =>
        !this.state.pendingReEnable[`${r.type}|${r.sku}`] &&
        this.state.notesEdits[`${r.type}|${r.sku}`] !== undefined &&
        this.state.notesEdits[`${r.type}|${r.sku}`] !== (r.notes || "")
      )
      .map(r => ({
        sku: r.sku,
        type: r.type,
        notes: this.getExistingRowNotes(r)
      }));

    const newDisables = this.state.pendingAdds.map(p => ({
      sku: p.sku,
      type: this.state.entity,
      notes: p.notes || ""
    }));

    return {
      disabled: [...newDisables, ...noteOnlyDisables],
      enabled: reEnableEntries
    };
  };

  pendingCount = () => {
    const d = this.pendingDiff();
    return d.disabled.length + d.enabled.length;
  };

  // ── Apply ───────────────────────────────────────────────────────────────

  applyChanges = async () => {
    const diff = this.pendingDiff();
    if (diff.disabled.length === 0 && diff.enabled.length === 0) return;

    this.setState({ submitting: true, errorMessage: "" });
    try {
      const res = await fetch("/api/Admin/ToggledSku/BulkUpdate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Disabled: diff.disabled,
          Enabled: diff.enabled
        })
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || "Apply failed");

      // Freeze the diff on screen — keep pendingAdds / pendingReEnable /
      // notesEdits intact so the grid still shows what's being applied.
      // The next status poll that detects Processing → Idle transition
      // clears all pending state and reloads the grid from the server.
      this.setState({
        submitting: false,
        submittedAt: Date.now()
      });
      this.loadStatus();
    } catch (err) {
      this.setState({ submitting: false, errorMessage: err.message || "Apply failed" });
    }
  };

  // True while a recently-submitted diff is waiting for the job to finish.
  // Used to lock the UI inputs in the frozen state.
  isFrozen = () => {
    if (!this.state.submittedAt) return false;
    const st = this.state.status.state;
    return st === "Queued" || st === "Processing";
  };

  // ── Cell renderers ──────────────────────────────────────────────────────

  cellCheckbox = (props) => {
    const row = props.dataItem;
    const key = `${row.type}|${row.sku}`;
    const reEnabling = !!this.state.pendingReEnable[key];
    const frozen = this.isFrozen();
    // Checked = still Disabled; unchecked = will re-enable on Apply
    return (
      <td>
        <input type="checkbox" checked={!reEnabling}
               disabled={frozen}
               onChange={() => this.toggleReEnable(row)} />
        {reEnabling && <span className="sku-toggle-pending"> (will re-enable)</span>}
      </td>
    );
  };

  cellNotes = (props) => {
    const row = props.dataItem;
    return (
      <td>
        <input
          type="text"
          className="sku-toggle-notes-input"
          maxLength={500}
          placeholder="optional reason…"
          value={this.getExistingRowNotes(row)}
          disabled={this.isFrozen()}
          onChange={(e) => this.setExistingRowNotes(row, e.target.value)}
        />
      </td>
    );
  };

  // ── Render helpers ──────────────────────────────────────────────────────

  renderStatusBadge() {
    const s = this.state.status;
    const cls = "admin-status " + (s.state || "Idle").toLowerCase();
    return <span className={cls}>{s.state || "Idle"}</span>;
  }

  renderApplyButton() {
    const pending = this.pendingCount();
    const idle = this.state.status.state === "Idle";
    const blocked = this.state.status.blockedByOtherLane;
    const disabled = pending === 0 || !idle || blocked || this.state.submitting;
    let label = "Apply Changes";
    if (pending > 0) label = `Apply Changes (${pending})`;
    if (!idle) label = `${this.state.status.state}…`;
    if (blocked) label = "Blocked — integration running";
    if (this.state.submitting) label = "Submitting…";
    return (
      <Button themeColor="primary" onClick={this.applyChanges} disabled={disabled}>
        {label}
      </Button>
    );
  }

  renderToolbar() {
    return (
      <div className="sku-toggle-toolbar">
        <label className="sku-toggle-entity-radio">
          <input type="radio" name="sku-toggle-entity" value="Frame"
                 checked={this.state.entity === "Frame"}
                 onChange={this.onEntityChange} />
          Frame
        </label>
        <label className="sku-toggle-entity-radio">
          <input type="radio" name="sku-toggle-entity" value="Fabric"
                 checked={this.state.entity === "Fabric"}
                 onChange={this.onEntityChange} />
          Fabric
        </label>
        <span className="sku-toggle-status-label">
          Status: {this.renderStatusBadge()}
          {this.state.status.lastRunTime && (
            <span className="sku-toggle-last-run"> — last applied {this.state.status.lastRunTime}</span>
          )}
        </span>
        <span className="sku-toggle-apply-wrap">
          {this.renderApplyButton()}
        </span>
      </div>
    );
  }

  renderSearchBar() {
    const frozen = this.isFrozen();
    return (
      <div className="sku-toggle-search-bar">
        <label>Search & add SKU to disabled list:</label>
        <input
          type="text"
          className="sku-toggle-search-input"
          placeholder={`Type a ${this.state.entity} SKU…`}
          value={this.state.searchQuery}
          onChange={this.onSearchChange}
          disabled={frozen}
          autoComplete="off"
        />
        {!frozen && this.state.searchHits.length > 0 && (
          <div className="sku-toggle-search-dropdown">
            {this.state.searchHits.map(hit => (
              <div key={hit.sku} className="sku-toggle-search-hit"
                   onClick={() => this.addSkuToDisabled(hit)}>
                <strong>{hit.sku}</strong> — {hit.name}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderFrozenBanner() {
    if (!this.isFrozen()) return null;
    return (
      <div className="sku-toggle-frozen-banner">
        <strong>{this.state.status.state}…</strong>
        &nbsp;Your changes are being applied. The grid is frozen and will refresh
        automatically when processing completes (~2 min after Apply). Galleries
        reflect the change at that time; search results may take up to <strong>~5 min total</strong>
        as the Azure Search indexer finishes rebuilding in the background.
      </div>
    );
  }

  renderPropagatingNote() {
    // Show "search still propagating" hint for ~3 min after the grid refreshes
    // (so the admin doesn't test search immediately and think nothing happened).
    if (this.isFrozen()) return null;
    if (!this.state.lastCompletedAt) return null;
    const PROPAGATION_WINDOW_MS = 3 * 60 * 1000;
    const elapsed = Date.now() - this.state.lastCompletedAt;
    if (elapsed >= PROPAGATION_WINDOW_MS) return null;
    return (
      <div className="sku-toggle-info-note">
        Grid updated. The search index can take up to <strong>~3 more min</strong> to
        finish propagating in Azure — if disabled SKUs still appear in search results,
        try again in a few minutes.
      </div>
    );
  }

  renderPendingAdds() {
    if (this.state.pendingAdds.length === 0) return null;
    const frozen = this.isFrozen();
    return (
      <div className="sku-toggle-pending-adds">
        <h4>Pending Disables ({this.state.pendingAdds.length})</h4>
        <table className="admin-table">
          <thead>
            <tr>
              <th></th>
              <th>SKU</th>
              <th>Product Name</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {this.state.pendingAdds.map(p => (
              <tr key={p.sku} className="sku-toggle-pending-row">
                <td>
                  <button className="sku-toggle-remove-btn"
                          disabled={frozen}
                          onClick={() => this.removePendingAdd(p.sku)}
                          title="Remove from pending">×</button>
                </td>
                <td>{p.sku}</td>
                <td>{p.name}</td>
                <td>
                  <input type="text" className="sku-toggle-notes-input"
                         maxLength={500} placeholder="optional reason…"
                         value={p.notes}
                         disabled={frozen}
                         onChange={(e) => this.setPendingAddNotes(p.sku, e.target.value)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    return (
      <div className="manage-sku-status">
        {this.renderToolbar()}

        {this.state.errorMessage && (
          <div className="sku-toggle-error">{this.state.errorMessage}</div>
        )}

        {this.renderFrozenBanner()}
        {this.renderPropagatingNote()}

        {this.renderSearchBar()}
        {this.renderPendingAdds()}

        <h4>
          Currently disabled — {this.state.entity} ({this.state.rows.length})
        </h4>

        <Grid
          data={this.state.rows}
          style={{ marginTop: 8 }}
          sortable={true}
          pageable={false}
        >
          <GridToolbar>
            <Button onClick={this.loadRows} disabled={this.state.loading}>
              Refresh
            </Button>
          </GridToolbar>
          <GridColumn field="" title="Keep Disabled" width="140" cell={this.cellCheckbox} />
          <GridColumn field="sku" title="SKU" width="130" />
          <GridColumn field="name" title="Product Name" />
          <GridColumn field="" title="Notes" cell={this.cellNotes} />
          <GridColumn field="dateModified" title="Modified" width="170" />
          <GridColumn field="modifiedBy" title="By" width="180" />
        </Grid>
      </div>
    );
  }
}
